'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { applyCnpjMask, isValidEmail } from '@/shared/utils';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { salvarPresenteBoasVindas } from '@/shared/utils/presenteBoasVindas';
import { reportError } from '@/shared/utils/reportError';
import { setStoredWorkspaceId } from '@/shared/utils/workspace';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { PRODUTOS, ROTA_LOGIN, ROTA_PLATAFORMA, STEPPER_LABELS, STEPPER_STEP_ORDER } from '../constants';
import { abrirAvisoNotificado, alternarProduto, carregarTermosOnboardingSelf, consultarCnpjOnboardingSelf, criarContaOnboardingSelf, fecharAvisoNotificado, hidratarRascunho, limparErroCriacao, resetCnpjStatus, selectOnboardingSelfAvisoNotificado, selectOnboardingSelfCanal, selectOnboardingSelfCnpjStatus, selectOnboardingSelfConta, selectOnboardingSelfCriandoConta, selectOnboardingSelfCriarContaErro, selectOnboardingSelfEmpresa, selectOnboardingSelfIncluirRespostas, selectOnboardingSelfProdutos, selectOnboardingSelfRascunhoHidratado, selectOnboardingSelfRazaoSocialReceita, selectOnboardingSelfSaldoPresente, selectOnboardingSelfStep, selectOnboardingSelfTermosStatus, selectOnboardingSelfTermosVersao, selectOnboardingSelfTermosVersaoAceita, selectOnboardingSelfVolume, setCampoEmpresa, setCanal, setStep, setTermosAceitos, setVolume } from '../store';
import type { CampoEmpresa, CanalAquisicao, OnboardingSelfStep, ProdutoId, VolumeCasosMes, WizardStepView } from '../types';
import { primeiroNomeDe, razaoCurtaDe } from '../utils/empresa';
import { acaoDoErroCriacaoConta } from '../utils/erroCriacaoConta';
import { rascunhoOnboardingSelf } from '../utils/rascunhoOnboardingSelf';
const DIGITOS_CNPJ = 14;
const resumoProdutos = (quantidade: number): string => {
  if (quantidade === 0) return 'Escolha ao menos um produto';
  if (quantidade === 1) return '1 produto selecionado';
  return `${quantidade} produtos selecionados`;
};
const cnpjCompleto = (cnpj: string) => normalizeCpfCnpj(cnpj).length === DIGITOS_CNPJ;
export const useOnboardingSelf = () => {
  const $ = _c(126);
  const dispatch = useAppDispatch();
  const step = useAppSelector(selectOnboardingSelfStep);
  const empresa = useAppSelector(selectOnboardingSelfEmpresa);
  const razaoSocialReceita = useAppSelector(selectOnboardingSelfRazaoSocialReceita);
  const cnpjStatus = useAppSelector(selectOnboardingSelfCnpjStatus);
  const termosVersao = useAppSelector(selectOnboardingSelfTermosVersao);
  const termosVersaoAceita = useAppSelector(selectOnboardingSelfTermosVersaoAceita);
  const termosStatus = useAppSelector(selectOnboardingSelfTermosStatus);
  const produtos = useAppSelector(selectOnboardingSelfProdutos);
  const volume = useAppSelector(selectOnboardingSelfVolume);
  const canal = useAppSelector(selectOnboardingSelfCanal);
  const criandoConta = useAppSelector(selectOnboardingSelfCriandoConta);
  const incluirRespostasDaOperacao = useAppSelector(selectOnboardingSelfIncluirRespostas);
  const criarContaErro = useAppSelector(selectOnboardingSelfCriarContaErro);
  const conta = useAppSelector(selectOnboardingSelfConta);
  const saldoPresente = useAppSelector(selectOnboardingSelfSaldoPresente);
  const avisoNotificadoAberto = useAppSelector(selectOnboardingSelfAvisoNotificado);
  const rascunhoHidratado = useAppSelector(selectOnboardingSelfRascunhoHidratado);
  const consultaCnpjRef = useRef(null);
  let t0;
  if ($[0] !== dispatch) {
    t0 = cnpj => {
      consultaCnpjRef.current?.abort();
      consultaCnpjRef.current = dispatch(consultarCnpjOnboardingSelf(cnpj));
    };
    $[0] = dispatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const consultarCnpj = t0;
  let t1;
  let t2;
  if ($[2] !== consultarCnpj || $[3] !== dispatch) {
    t1 = () => {
      const rascunho = rascunhoOnboardingSelf.ler();
      dispatch(hidratarRascunho(rascunho));
      if (rascunho && cnpjCompleto(rascunho.empresa.cnpj)) {
        consultarCnpj(rascunho.empresa.cnpj);
      }
    };
    t2 = [dispatch, consultarCnpj];
    $[2] = consultarCnpj;
    $[3] = dispatch;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] !== canal || $[7] !== conta || $[8] !== empresa || $[9] !== produtos || $[10] !== rascunhoHidratado || $[11] !== termosVersaoAceita || $[12] !== volume) {
    t3 = () => {
      if (!rascunhoHidratado) {
        return;
      }
      if (conta) {
        rascunhoOnboardingSelf.limpar();
        return;
      }
      rascunhoOnboardingSelf.salvar({
        empresa,
        produtos,
        volume,
        canal,
        termosVersaoAceita
      });
    };
    t4 = [rascunhoHidratado, conta, empresa, produtos, volume, canal, termosVersaoAceita];
    $[6] = canal;
    $[7] = conta;
    $[8] = empresa;
    $[9] = produtos;
    $[10] = rascunhoHidratado;
    $[11] = termosVersaoAceita;
    $[12] = volume;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[15] !== dispatch || $[16] !== step || $[17] !== termosStatus) {
    t5 = () => {
      if (step !== "inicio" && termosStatus === "idle") {
        dispatch(carregarTermosOnboardingSelf());
      }
    };
    t6 = [dispatch, step, termosStatus];
    $[15] = dispatch;
    $[16] = step;
    $[17] = termosStatus;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useEffect(t5, t6);
  const indiceAtual = STEPPER_STEP_ORDER[step] ?? 0;
  let t7;
  if ($[20] !== indiceAtual) {
    t7 = STEPPER_LABELS.map((label, indice) => ({
      label,
      filled: indice <= indiceAtual,
      active: indice === indiceAtual
    }));
    $[20] = indiceAtual;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const stepperSteps = t7;
  let t8;
  if ($[22] !== produtos) {
    t8 = PRODUTOS.filter(produto => produtos.includes(produto.id));
    $[22] = produtos;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const produtosEscolhidos = t8;
  let t9;
  if ($[24] !== empresa.responsavelEmail) {
    t9 = isValidEmail(empresa.responsavelEmail);
    $[24] = empresa.responsavelEmail;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const emailValido = t9;
  let t10;
  if ($[26] !== emailValido || $[27] !== empresa.responsavelEmail) {
    t10 = empresa.responsavelEmail.trim().length > 0 && !emailValido;
    $[26] = emailValido;
    $[27] = empresa.responsavelEmail;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const emailInvalido = t10;
  let t11;
  if ($[29] !== emailValido || $[30] !== empresa.cnpj || $[31] !== empresa.responsavelNome || $[32] !== empresa.termosAceitos) {
    t11 = cnpjCompleto(empresa.cnpj) && empresa.responsavelNome.trim().length > 2 && emailValido && empresa.termosAceitos;
    $[29] = emailValido;
    $[30] = empresa.cnpj;
    $[31] = empresa.responsavelNome;
    $[32] = empresa.termosAceitos;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const empresaPreenchida = t11;
  let t12;
  if ($[34] !== cnpjStatus || $[35] !== empresaPreenchida || $[36] !== razaoSocialReceita || $[37] !== termosVersao) {
    t12 = empresaPreenchida && cnpjStatus === "ok" && termosVersao !== null && razaoSocialReceita.trim().length > 2;
    $[34] = cnpjStatus;
    $[35] = empresaPreenchida;
    $[36] = razaoSocialReceita;
    $[37] = termosVersao;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const podeAvancarEmpresa = t12;
  const podeAvancarProdutos = produtos.length > 0;
  let t13;
  if ($[39] !== empresa.responsavelNome) {
    t13 = primeiroNomeDe(empresa.responsavelNome);
    $[39] = empresa.responsavelNome;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const primeiroNome = t13;
  let t14;
  if ($[41] !== razaoSocialReceita) {
    t14 = razaoCurtaDe(razaoSocialReceita);
    $[41] = razaoSocialReceita;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const razaoCurta = t14;
  let t15;
  if ($[43] !== criarContaErro) {
    t15 = criarContaErro ? acaoDoErroCriacaoConta(criarContaErro.codigo) : null;
    $[43] = criarContaErro;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const acaoErroCriacao = t15;
  let t16;
  if ($[45] !== dispatch) {
    t16 = destino => dispatch(setStep(destino));
    $[45] = dispatch;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const goTo = t16;
  let t17;
  if ($[47] !== cnpjStatus || $[48] !== consultarCnpj || $[49] !== dispatch) {
    t17 = (campo, valor) => {
      if (campo !== "cnpj") {
        dispatch(setCampoEmpresa({
          campo,
          valor
        }));
        return;
      }
      const mascarado = applyCnpjMask(valor);
      dispatch(setCampoEmpresa({
        campo,
        valor: mascarado
      }));
      if (cnpjCompleto(mascarado)) {
        consultarCnpj(mascarado);
      } else {
        if (cnpjStatus !== "idle") {
          consultaCnpjRef.current?.abort();
          dispatch(resetCnpjStatus());
        }
      }
    };
    $[47] = cnpjStatus;
    $[48] = consultarCnpj;
    $[49] = dispatch;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  const atualizarCampo = t17;
  let t18;
  if ($[51] !== dispatch) {
    t18 = () => dispatch(carregarTermosOnboardingSelf());
    $[51] = dispatch;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  const recarregarTermos = t18;
  let t19;
  if ($[53] !== consultarCnpj || $[54] !== empresa.cnpj) {
    t19 = () => consultarCnpj(empresa.cnpj);
    $[53] = consultarCnpj;
    $[54] = empresa.cnpj;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  const consultarCnpjDeNovo = t19;
  let t20;
  if ($[56] !== dispatch || $[57] !== empresa.termosAceitos) {
    t20 = () => dispatch(setTermosAceitos(!empresa.termosAceitos));
    $[56] = dispatch;
    $[57] = empresa.termosAceitos;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  const alternarTermos = t20;
  let t21;
  if ($[59] !== dispatch) {
    t21 = id => dispatch(alternarProduto(id));
    $[59] = dispatch;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  const alternarProdutoSelecionado = t21;
  let t22;
  if ($[61] !== dispatch) {
    t22 = id_0 => dispatch(setVolume(id_0));
    $[61] = dispatch;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  const escolherVolume = t22;
  let t23;
  if ($[63] !== dispatch) {
    t23 = id_1 => dispatch(setCanal(id_1));
    $[63] = dispatch;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  const escolherCanal = t23;
  let t24;
  if ($[65] !== dispatch) {
    t24 = () => dispatch(criarContaOnboardingSelf({
      incluirRespostasDaOperacao: true
    }));
    $[65] = dispatch;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  const criarConta = t24;
  let t25;
  if ($[67] !== dispatch) {
    t25 = () => dispatch(criarContaOnboardingSelf({
      incluirRespostasDaOperacao: false
    }));
    $[67] = dispatch;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  const pularECriarConta = t25;
  let t26;
  if ($[69] !== dispatch || $[70] !== incluirRespostasDaOperacao) {
    t26 = () => dispatch(criarContaOnboardingSelf({
      incluirRespostasDaOperacao
    }));
    $[69] = dispatch;
    $[70] = incluirRespostasDaOperacao;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  const repetirCriacaoConta = t26;
  let t27;
  if ($[72] !== criarContaErro?.codigo || $[73] !== dispatch) {
    t27 = () => {
      const destino_0 = criarContaErro?.codigo === "CNPJ_NAO_ENCONTRADO" ? "empresa" : "operacao";
      dispatch(limparErroCriacao());
      dispatch(setStep(destino_0));
    };
    $[72] = criarContaErro?.codigo;
    $[73] = dispatch;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  const voltarDoErroDeCriacao = t27;
  const irParaLogin = _temp;
  let t28;
  if ($[75] !== conta || $[76] !== empresa.responsavelNome || $[77] !== primeiroNome) {
    t28 = () => {
      if (!conta) {
        return;
      }
      salvarPresenteBoasVindas({
        usuarioId: conta.usuarioId,
        saldoNotificacoes: conta.saldoPresente,
        primeiroNome: empresa.responsavelNome.trim() ? primeiroNome : ""
      });
      if (conta.sessaoCriada) {
        ;
        try {
          setStoredWorkspaceId(conta.espacoDeTrabalhoId);
        } catch (t29) {
          const error = t29;
          reportError(error instanceof Error ? error : new Error(String(error)), {
            source: "onboardingSelf.entrarNaPlataforma"
          });
        }
      }
      window.location.assign(conta.sessaoCriada ? ROTA_PLATAFORMA : ROTA_LOGIN);
    };
    $[75] = conta;
    $[76] = empresa.responsavelNome;
    $[77] = primeiroNome;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  const entrarNaPlataforma = t28;
  let t29;
  if ($[79] !== dispatch) {
    t29 = () => dispatch(abrirAvisoNotificado());
    $[79] = dispatch;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  const abrirAviso = t29;
  let t30;
  if ($[81] !== dispatch) {
    t30 = () => dispatch(fecharAvisoNotificado());
    $[81] = dispatch;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  const fecharAviso = t30;
  const t31 = termosStatus === "error";
  let t32;
  if ($[83] !== produtos.length) {
    t32 = resumoProdutos(produtos.length);
    $[83] = produtos.length;
    $[84] = t32;
  } else {
    t32 = $[84];
  }
  const t33 = conta !== null;
  let t34;
  if ($[85] !== abrirAviso || $[86] !== acaoErroCriacao || $[87] !== alternarProdutoSelecionado || $[88] !== alternarTermos || $[89] !== atualizarCampo || $[90] !== avisoNotificadoAberto || $[91] !== canal || $[92] !== cnpjStatus || $[93] !== consultarCnpjDeNovo || $[94] !== criandoConta || $[95] !== criarConta || $[96] !== criarContaErro || $[97] !== emailInvalido || $[98] !== emailValido || $[99] !== empresa || $[100] !== empresaPreenchida || $[101] !== entrarNaPlataforma || $[102] !== escolherCanal || $[103] !== escolherVolume || $[104] !== fecharAviso || $[105] !== goTo || $[106] !== podeAvancarEmpresa || $[107] !== podeAvancarProdutos || $[108] !== primeiroNome || $[109] !== produtos || $[110] !== produtosEscolhidos || $[111] !== pularECriarConta || $[112] !== rascunhoHidratado || $[113] !== razaoCurta || $[114] !== razaoSocialReceita || $[115] !== recarregarTermos || $[116] !== repetirCriacaoConta || $[117] !== saldoPresente || $[118] !== step || $[119] !== stepperSteps || $[120] !== t31 || $[121] !== t32 || $[122] !== t33 || $[123] !== voltarDoErroDeCriacao || $[124] !== volume) {
    t34 = {
      step,
      empresa,
      razaoSocialReceita,
      cnpjStatus,
      termosIndisponiveis: t31,
      produtos,
      produtosEscolhidos,
      produtosResumo: t32,
      volume,
      canal,
      criandoConta,
      criarContaErro,
      acaoErroCriacao,
      contaCriada: t33,
      saldoPresente,
      avisoNotificadoAberto,
      rascunhoHidratado,
      stepperSteps,
      emailValido,
      emailInvalido,
      empresaPreenchida,
      podeAvancarEmpresa,
      podeAvancarProdutos,
      primeiroNome,
      razaoCurta,
      goTo,
      atualizarCampo,
      consultarCnpjDeNovo,
      recarregarTermos,
      alternarTermos,
      alternarProdutoSelecionado,
      escolherVolume,
      escolherCanal,
      criarConta,
      pularECriarConta,
      repetirCriacaoConta,
      voltarDoErroDeCriacao,
      irParaLogin,
      entrarNaPlataforma,
      abrirAviso,
      fecharAviso
    };
    $[85] = abrirAviso;
    $[86] = acaoErroCriacao;
    $[87] = alternarProdutoSelecionado;
    $[88] = alternarTermos;
    $[89] = atualizarCampo;
    $[90] = avisoNotificadoAberto;
    $[91] = canal;
    $[92] = cnpjStatus;
    $[93] = consultarCnpjDeNovo;
    $[94] = criandoConta;
    $[95] = criarConta;
    $[96] = criarContaErro;
    $[97] = emailInvalido;
    $[98] = emailValido;
    $[99] = empresa;
    $[100] = empresaPreenchida;
    $[101] = entrarNaPlataforma;
    $[102] = escolherCanal;
    $[103] = escolherVolume;
    $[104] = fecharAviso;
    $[105] = goTo;
    $[106] = podeAvancarEmpresa;
    $[107] = podeAvancarProdutos;
    $[108] = primeiroNome;
    $[109] = produtos;
    $[110] = produtosEscolhidos;
    $[111] = pularECriarConta;
    $[112] = rascunhoHidratado;
    $[113] = razaoCurta;
    $[114] = razaoSocialReceita;
    $[115] = recarregarTermos;
    $[116] = repetirCriacaoConta;
    $[117] = saldoPresente;
    $[118] = step;
    $[119] = stepperSteps;
    $[120] = t31;
    $[121] = t32;
    $[122] = t33;
    $[123] = voltarDoErroDeCriacao;
    $[124] = volume;
    $[125] = t34;
  } else {
    t34 = $[125];
  }
  return t34;
};
export type UseOnboardingSelfReturn = ReturnType<typeof useOnboardingSelf>;
function _temp() {
  return window.location.assign(ROTA_LOGIN);
}